import { type GetServerSidePropsContext } from 'next';
import { getServerSession } from 'next-auth';
import { signIn } from 'next-auth/react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { authOptions } from '../api/auth/[...nextauth]';

type ProntoProvider = {
  id: string;
  name: string;
  type: string;
};
type ProntoProviders = Record<string, ProntoProvider>;

export default function SignIn({
  providers,
  randomName,
}: {
  providers: ProntoProviders;
  randomName: string;
}) {
  const params = useSearchParams();
  const callbackUrl =
    params.get('callbackUrl') || process.env.NEXT_PUBLIC_BASE_PATH || '/';

  return (
    <div className="rd__auth">
      <div className="rd__home-content">
        <h1 className="rd__home-heading">
          <span style={{color: '#fff', marginBottom:'1rem'}}>{'[video chat]'}</span>
        </h1>
        <ul className="rd__auth-list">
          {Object.values(providers || {}).map((provider) => {
            if (provider.id === 'stream-demo-login') {
              return (
                <GuestLoginItem
                  key={provider.id}
                  provider={provider}
                  randomName={randomName}
                  callbackUrl={callbackUrl}
                />
              );
            }
            return null;
          })}
        </ul>
      </div>
    </div>
  );
}

const GuestLoginItem = (props: {
  provider: ProntoProvider;
  randomName: string;
  callbackUrl: string;
}) => {
  const { provider, randomName, callbackUrl } = props;
  const [name, setName] = useState(randomName);
  const logIn = useCallback(
    () => signIn(provider.id, { name, callbackUrl }),
    [callbackUrl, name, provider.id],
  );
  const params = useSearchParams();
  const fromQR = params.get('from_qr');
  useEffect(() => {
    if (fromQR) {
      // log in immediately if from QR code - no need to enter name
      // as it anyway doesn't have any effect inside the mobile app
      // https://getstream.slack.com/archives/C01CG3P80LV/p1704390875184309
      logIn().catch((err) => {
        console.error('Error logging in:', err);
      });
    }
  }, [fromQR, logIn]);

  return (
    <li className="rd__auth-item rd__auth-item--guest-login">
      <div className="rd__auth-item--guest_name_wrapper">
        <span style={{ margin: '0 0.25rem 0.25rem 0', whiteSpace: 'nowrap' }}>your name:</span>
        <input
          className="rd__input"
          type="text"
          value={name}
          maxLength={25}
          onChange={(e) => setName(e.target.value)}
          onKeyUp={(e) => e.key === 'Enter' && logIn()}
          placeholder="name"
        />
      </div>
      <button
        className="rd__button rd__button--primary rd__auth-provider"
        onClick={logIn}
        data-testid="guest-sign-in-button"
      >
        continue
      </button>
    </li>
  );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const session = await getServerSession(context.req, context.res, authOptions);
  if (session) {
    const { query } = context;
    const params = new URLSearchParams(query as Record<string, string>);
    // redirect to "base path" if already signed in
    return {
      redirect: {
        destination: `/?${params.toString()}`,
      },
    };
  }

  const providers = authOptions.providers.reduce<ProntoProviders>(
    (acc, { id, name, type }) => {
      acc[id] = { id, name, type };
      return acc;
    },
    {},
  );

  return {
    props: {
      providers,
      randomName: 'guest',
    },
  };
}
